<template>
  <div class="questions-page row">
    <div class="left-wrap">
      <header-sources :header-title="$t('SOURCES.HEADER')" />
      <question-table
        :questions="questionList"
        :sort-by="meta.sortBy"
        :sort-order="meta.sortOrder"
        :count-unresolved="countUnresolved"
        :is-loading="uiFlags.isFetching"
        :categories="filterCategoryList"
        :selected-category="selectedCategory"
        @show-create-question="onOpenCreateQuestion"
        @on-update-sortby="onUpdateSortby"
        @on-update-filter-category="onUpdateFilterCategory"
        @on-delete-click="onDeleteClick"
        @on-toggle-enabled-click="onOnToggleEnabledClick"
        @show-edit-question="onOpenEditQuestion"
      />
      <table-footer
        :current-page="Number(meta.currentPage)"
        :total-count="meta.totalEntries"
        :page-size="meta.perPage"
        @page-change="onPageChange"
      />
    </div>
    <woot-modal
      :show.sync="showCreateQuestion"
      :on-close="onHideCreateQuestion"
    >
      <create-question :categories="categories" @close="onHideCreateQuestion" />
    </woot-modal>
    <woot-modal :show.sync="showEditQuestion" :on-close="onHideEditQuestion">
      <edit-question
        :row-data="this.loadRowData"
        :categories="categories"
        @close="onHideEditQuestion"
      />
    </woot-modal>
    <woot-delete-modal
      :show.sync="showDeletePopup"
      :on-close="closeDeletePopup"
      :on-confirm="confirmDeletion"
      :title="$t('QUESTION_SOURCES.ACTIONS.DELETE.CONFIRM.TITLE')"
      :message="$t('QUESTION_SOURCES.ACTIONS.DELETE.CONFIRM.MESSAGE')"
      :confirm-text="$t('QUESTION_SOURCES.ACTIONS.DELETE.CONFIRM.YES')"
      :reject-text="$t('QUESTION_SOURCES.ACTIONS.DELETE.CONFIRM.NO')"
    />
  </div>
</template>
<script>
import { mapGetters } from 'vuex';
import globalConfigMixin from 'shared/mixins/globalConfigMixin';
import HeaderSources from './components/Header.vue';
import QuestionTable from './components/QuestionTable.vue';
import TableFooter from 'dashboard/components/widgets/TableFooter';
import CreateQuestion from './components/createQuestion/Index.vue';
import EditQuestion from './components/editQuestion/index.vue';

const DEFAULT_PAGE = 1;

export default {
  name: 'QuestionsSources',
  components: {
    HeaderSources,
    TableFooter,
    QuestionTable,
    CreateQuestion,
    EditQuestion,
  },
  mixins: [globalConfigMixin],
  computed: {
    ...mapGetters({
      countUnresolved: 'questions/getCountUnresolved',
      questionList: 'questions/getQuestions',
      uiFlags: 'questions/getUIFlags',
      meta: 'questions/getMeta',
      categories: 'questionCategories/getCategories',
    }),
    pageParameter() {
      const selectedPageNumber = Number(this.$route.query?.page);
      return !Number.isNaN(selectedPageNumber) &&
        selectedPageNumber >= DEFAULT_PAGE
        ? selectedPageNumber
        : DEFAULT_PAGE;
    },
  },
  data() {
    return {
      selectedCategory: {},
      currentQuestion: {},
      showDeletePopup: false,
      showCreateQuestion: false,
      showEditQuestion: false,
      loadRowData: {},
      filterCategoryList: [],
    };
  },
  async mounted() {
    this.fetchQuestions(this.pageParameter, this.meta.sortBy);
    await this.$store.dispatch('questionCategories/get');
    this.filterCategoryList = [
      {
        name: 'All Categories',
        question_count: this.categories
          .map(item => item.question_count)
          .reduce((prev, next) => prev + next),
      },
      ...this.categories,
    ];
    this.selectedCategory = this.filterCategoryList[0];
  },
  methods: {
    onPageChange(page) {
      this.fetchQuestions(page, this.meta.sortBy, this.meta.sortOrder);
    },
    updatePageParam(page) {
      window.history.pushState({}, null, `${this.$route.path}?page=${page}`);
    },
    onUpdateSortby(sortBy, sortOrder) {
      let page = this.meta.currentPage;
      if (sortBy !== this.meta.sortBy || sortOrder !== this.meta.sortOrder) {
        page = DEFAULT_PAGE;
      }
      this.fetchQuestions(page, sortBy, sortOrder);
    },
    fetchQuestions(page, sortBy, sortOrder, filter) {
      this.updatePageParam(page);
      const requestParams = {
        page,
        perPage: this.meta.perPage,
        sortBy: sortBy ?? this.meta.sortBy,
        sortOrder: sortOrder ?? this.meta.sortOrder,
        filter: filter ?? this.meta.filter,
      };
      this.$store.dispatch('questions/get', requestParams);
    },
    onOpenCreateQuestion() {
      this.showCreateQuestion = true;
    },
    onHideCreateQuestion() {
      this.showCreateQuestion = false;
    },
    onOpenEditQuestion(rowData) {
      this.loadRowData = rowData;
      this.showEditQuestion = true;
    },
    onHideEditQuestion() {
      this.showEditQuestion = false;
    },
    onUpdateFilterCategory(data) {
      this.selectedCategory = data;
      let filter = '';
      if (this.selectedCategory.id) {
        filter = this.selectedCategory.id;
      }
      let page = this.meta.currentPage;
      if (filter !== this.meta.filter) {
        page = DEFAULT_PAGE;
      }
      this.fetchQuestions(page, this.meta.sortBy, this.meta.sortOrder, filter);
    },
    onDeleteClick(question) {
      this.currentQuestion = question;
      this.showDeletePopup = true;
    },
    closeDeletePopup() {
      this.currentQuestion = {};
      this.showDeletePopup = false;
    },
    async confirmDeletion() {
      await this.deleteDocument(this.currentQuestion);
      this.closeDeletePopup();
    },
    async deleteDocument(question) {
      try {
        await this.$store.dispatch('questions/delete', question);
        this.showAlert(
          this.$t('QUESTION_SOURCES.ACTIONS.DELETE.API.SUCCESS_MESSAGE')
        );
      } catch (error) {
        this.showAlert(
          this.$t('QUESTION_SOURCES.ACTIONS.DELETE.API.ERROR_MESSAGE')
        );
      }
    },
    async onOnToggleEnabledClick(question) {
      try {
        await this.$store.dispatch('questions/toggleEnabled', question);
        this.showAlert(
          this.$t('QUESTION_SOURCES.ACTIONS.TOGGLE_ENABLED.API.SUCCESS_MESSAGE')
        );
      } catch (error) {
        this.showAlert(
          this.$t('QUESTION_SOURCES.ACTIONS.TOGGLE_ENABLED.API.ERROR_MESSAGE')
        );
      }
    },
    // Show SnackBar
    showAlert(message) {
      this.currentQuestion = {};
      bus.$emit('newToastMessage', message);
    },
  },
};
</script>

<style scoped lang="scss">
.questions-page {
  width: 100%;
  background: var(--s-25);
  overflow: auto;
}

.left-wrap {
  display: flex;
  flex-direction: column;
  height: 100%;
  width: 100%;
}
.footer {
  margin-top: auto;
  background: var(--white);
}
</style>
